import { useEffect, useState, type FormEvent } from 'react'
import { ApiClientError, useToast } from 'grabbitbot-legacy-ui'
import { tradingClient } from '../../api'
import type { ExchangeKeyView } from '../../api/tradingTypes'
import { buildExchangeKeyPatch } from './exchangeKeyPatch'
import { keyIsComplete, useDefaultConfigs } from './settingsData'
import { ExchangeTabs, NoBotsNotice, PublicIpNotice, SettingsTabs } from './settingsShared'
import { failureText } from '../../api/errorMessages'
import { GRABBITBOT_ERROR_MESSAGES } from '../../api/errorMessages'

function StoredFlag({ value }: { value: number | undefined }) {
  const stored = Number(value) === 1
  return (
    <span className={stored ? 'portal-flag portal-flag--ok' : 'portal-flag portal-flag--missing'}>
      {stored ? 'set' : 'not set'}
    </span>
  )
}

/**
 * Equivalent of legacy `V3SettingsExchangeComponent` (route
 * `settings/exchange`): per exchange account, the exchange API key + secret
 * the spotbot trades with. BUSINESS-CRITICAL / SECURITY.
 *
 * Deliberate differences from legacy (security fixes, B2 contract §23-24 and
 * audit §6.4 / §10 defect 0 — ticket cf5ab560):
 *  - WRITE-ONLY. Legacy loaded the DECRYPTED key and secret into two
 *    `[(ngModel)]` inputs (the secret with a show/hide eye toggle). The
 *    backend no longer returns any stored value; this page shows only
 *    "set" / "not set" from the `has*` flags and never renders, stores or
 *    logs a credential value. The inputs start empty every time.
 *  - Only typed fields are sent (see `buildExchangeKeyPatch`). Legacy sent
 *    its whole in-memory object back, which was harmless only because that
 *    object held the decrypted originals.
 *  - `type="password"` + `autoComplete="new-password"` on both inputs, so the
 *    browser neither shows nor offers to save/fill them; the values are
 *    cleared from React state right after a save attempt.
 *  - The only stored value shown is the PUBLIC key masked by the backend
 *    (`exchangeKeyHint`, first 4 + "…" + last 4), so the user can find it in
 *    the exchange's API management list; the secret is never displayed.
 *  - `customField1` had no input in legacy either; its presence is shown
 *    read-only.
 */
export function SettingsExchangePage() {
  const { configs, loading, error, exchangeId, setExchangeId, reload } = useDefaultConfigs()
  const { showToast } = useToast()
  const [keyView, setKeyView] = useState<ExchangeKeyView | null>(null)
  const [keyLoading, setKeyLoading] = useState(false)
  const [keyError, setKeyError] = useState<unknown>(null)
  const [typedKey, setTypedKey] = useState('')
  const [typedSecret, setTypedSecret] = useState('')
  const [saving, setSaving] = useState(false)
  const [keyReload, setKeyReload] = useState(0)

  useEffect(() => {
    // Legacy `setExchangeId()`: drop the old answer, then view the key.
    setKeyView(null)
    setTypedKey('')
    setTypedSecret('')
    if (!exchangeId) return
    let cancelled = false
    setKeyLoading(true)
    setKeyError(null)
    tradingClient
      .viewExchangeKey(exchangeId)
      .then((result) => {
        if (!cancelled) setKeyView(result)
      })
      .catch((err: unknown) => {
        if (!cancelled) setKeyError(err ?? true)
      })
      .finally(() => {
        if (!cancelled) setKeyLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [exchangeId, keyReload])

  const patch = buildExchangeKeyPatch({ exchangeKey: typedKey, exchangeSecret: typedSecret })
  const nothingTyped = Object.keys(patch).length === 0

  async function handleSave(event: FormEvent) {
    event.preventDefault()
    if (nothingTyped || !exchangeId) return
    setSaving(true)
    try {
      const result = await tradingClient.modifyExchangeKey(exchangeId, patch)
      setKeyView(result)
      showToast('Exchange key saved', 'success')
      // Legacy `save()` -> `refreshData()`: reload the list (key status icons).
      reload()
      setKeyReload((n) => n + 1)
    } catch (err) {
      // Never echo anything from the request or an unknown backend message
      // here (a key was just typed): only the fixed code table / network text
      // may add a reason; everything else stays the fixed sentence.
      const known = err instanceof ApiClientError && (err.status === 0 || GRABBITBOT_ERROR_MESSAGES[err.code] !== undefined)
      showToast(
        known ? failureText(err, 'Could not save the exchange key') : 'Could not save the exchange key. Please check the values and try again.',
        'error',
      )
    } finally {
      setTypedKey('')
      setTypedSecret('')
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="portal-page-title">
        <h1>Settings - Exchange</h1>
      </div>
      <div className="portal-card">
        <SettingsTabs />
        <PublicIpNotice />

        {loading && <p>Loading…</p>}
        {error && <p role="alert">{failureText(error, 'Could not load your exchange accounts')}</p>}
        {configs && configs.length === 0 && !loading && !error && <NoBotsNotice withLink />}

        {configs && configs.length > 0 && (
          <ExchangeTabs
            configs={configs}
            exchangeId={exchangeId}
            onSelect={setExchangeId}
            disabled={saving}
            renderSuffix={(config) => (
              <span
                className={keyIsComplete(config) ? 'portal-flag portal-flag--ok' : 'portal-flag portal-flag--missing'}
                aria-label={keyIsComplete(config) ? 'key set' : 'key missing'}
              >
                {keyIsComplete(config) ? '✓' : '✕'}
              </span>
            )}
          />
        )}

        {keyLoading && <p>Loading…</p>}
        {keyError !== null && <p role="alert">{failureText(keyError, 'Could not load the exchange key status')}</p>}

        {keyView && exchangeId && (
          <form className="portal-form portal-settings-form" onSubmit={handleSave} autoComplete="off">
            <dl className="portal-kv">
              <dt>Exchange Key</dt>
              <dd>
                <StoredFlag value={keyView.hasExchangeKey} />
                {keyView.exchangeKeyHint ? (
                  <span className="portal-text-muted" data-testid="exchange-key-hint">
                    {' '}
                    ({keyView.exchangeKeyHint})
                  </span>
                ) : null}
              </dd>
              <dt>Exchange Secret</dt>
              <dd>
                <StoredFlag value={keyView.hasExchangeSecret} />
              </dd>
              <dt>Custom field</dt>
              <dd>
                <StoredFlag value={keyView.hasCustomField1} />
              </dd>
            </dl>
            <p className="portal-text-muted">
              Find this key in Binance → API Management; the secret is never shown. The secret is never
              displayed. Type a new value only for what you want to change — an empty
              field keeps the stored value.
            </p>
            <label>
              New Exchange Key
              <input
                type="password"
                name="new-exchange-key"
                autoComplete="new-password"
                spellCheck={false}
                value={typedKey}
                onChange={(e) => setTypedKey(e.target.value)}
              />
            </label>
            <label>
              New Exchange Secret
              <input
                type="password"
                name="new-exchange-secret"
                autoComplete="new-password"
                spellCheck={false}
                value={typedSecret}
                onChange={(e) => setTypedSecret(e.target.value)}
              />
            </label>
            <div>
              <button type="submit" disabled={saving || nothingTyped}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
